import { useState, useEffect, FormEvent, ReactElement } from "react";

import Head from "next/head";
import NextLink from "next/link";

import {
  Alert,
  AlertIcon,
  Button,
  Container,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";

import { useRouter } from "next/router";
import MainIndex from "components/layout/main-index";
import { SuccessLogo } from "components/global/logo";
import { HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import { Auth } from "aws-amplify";
import { GetServerSideProps } from "next";

interface SignInState {
  email: string;
}

interface ResetPasswordDataState {
  newPass: string;
  confNewPass: string;
}

interface SignInStatusState {
  status: "success" | "error" | "info";
  message?: string;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { authCode, email } = context.query;

  return {
    props: {
      email: email && typeof email === "string" ? email : null,
      authCode: authCode && typeof authCode === "string" ? authCode : null,
    },
  };
};

const ResetPasswordPage = (props: {
  email: string | null;
  authCode: string | null;
}) => {
  const { email, authCode } = props;
  const router = useRouter();

  const [signInData, setSignInData] = useState<SignInState>({
    email: "",
  });
  const [resetPasswordData, setResetPasswordData] =
    useState<ResetPasswordDataState>({
      newPass: "",
      confNewPass: "",
    });
  const [signInStatus, setSignInStatus] = useState<
    SignInStatusState | undefined
  >();
  const [showPass, setShowPass] = useState<{
    pass: boolean;
    confPass: boolean;
  }>({ pass: false, confPass: false });
  const [loading, setLoading] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(authCode && email ? 2 : 0);

  const handleInputChange = (event: any) => {
    setSignInData((s) => ({ ...s, [event.target.name]: event.target.value }));
  };

  const handleResetPasswordChange = (event: any) => {
    setResetPasswordData((s) => ({
      ...s,
      [event.target.name]: event.target.value,
    }));
  };

  const forgotPasswordSubmit = async () => {
    if (signInData.email && signInData.email !== "") {
      try {
        Auth.forgotPassword(signInData.email)
          .then((data) => console.log(data))
          .catch((err) => console.log(err));
      } catch (error) {
        console.log(error);
      }
    }
  };

  const forgotPasswordSubmitNewPassword = async () => {
    if (
      email &&
      authCode &&
      resetPasswordData.newPass == resetPasswordData.confNewPass
    ) {
      try {
        Auth.forgotPasswordSubmit(
          String(email),
          String(authCode),
          resetPasswordData.newPass
        )
          .then((data) => {
            setSignInStatus({
              status: "success",
              message:
                "Your password has been changed. Redirecting to signin page...",
            });
            setTimeout(() => {
              router.push("/signin");
            }, 2000);
          })
          .catch((err) =>
            setSignInStatus({
              status: "error",
              message: err.message,
            })
          );
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    if (authCode && email) {
      setTabIndex(2);
    }
  }, [authCode, email]);

  return (
    <>
      <Container width={"full"} maxW={"100vw"} maxH={"90vh"}>
        <Flex
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
          height={"90vh"}
          gap={8}
        >
          <Container maxW={"container.sm"} p={6} rounded={"xl"}>
            <Tabs index={tabIndex} variant={"unstyled"} p={0}>
              <TabPanels>
                <TabPanel p={0}>
                  <Heading
                    as={"h2"}
                    size={"xl"}
                    textTransform={"uppercase"}
                    textAlign={"center"}
                    mb={8}
                  >
                    Forget Password
                  </Heading>
                  <Text mb={4}>
                    Enter the email associated with yout account and we&apos;ll
                    send and email with instruction to reset your password.
                  </Text>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      forgotPasswordSubmit();
                      setTabIndex(1);
                    }}
                  >
                    <Stack gap={4}>
                      <FormControl isRequired>
                        <FormLabel>Email address</FormLabel>
                        <Input
                          name={"email"}
                          id={"email"}
                          onChange={(e) => handleInputChange(e)}
                          value={signInData.email}
                          variant={"main"}
                          type={"email"}
                          placeholder={"Enter your valid email address"}
                        />
                      </FormControl>

                      {signInStatus && (
                        <Alert status={signInStatus.status}>
                          <AlertIcon />
                          {signInStatus.message}
                        </Alert>
                      )}
                      <Button
                        type={"submit"}
                        variant={"gradient"}
                        isLoading={loading}
                      >
                        Reset Password
                      </Button>
                      <Flex justifyContent={"space-between"}>
                        <Text>
                          Return to{" "}
                          <Link
                            as={NextLink}
                            href={`/signin`}
                            color={"main.magenta"}
                          >
                            <strong>Sign in page</strong>
                          </Link>
                        </Text>
                      </Flex>
                    </Stack>
                  </form>
                </TabPanel>
                <TabPanel p={0}>
                  <VStack gap={6}>
                    <SuccessLogo size="sm" />
                    <VStack gap={2}>
                      <Heading as={"h3"} fontWeight={"semibold"} size={"xl"}>
                        Check your email!
                      </Heading>
                      <Text>
                        We have sent link to your email to reset your password.
                      </Text>
                    </VStack>
                    <Button
                      as={NextLink}
                      href={"/signin"}
                      variant={"gradient"}
                      px={12}
                      size={"md"}
                    >
                      Back to Login
                    </Button>
                  </VStack>
                </TabPanel>
                <TabPanel p={0}>
                  <Heading
                    as={"h2"}
                    size={"xl"}
                    textTransform={"uppercase"}
                    textAlign={"center"}
                    mb={8}
                  >
                    Reset Password
                  </Heading>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      forgotPasswordSubmitNewPassword();
                    }}
                  >
                    <Stack gap={4}>
                      <FormControl isRequired>
                        <FormLabel>New Password</FormLabel>
                        <InputGroup>
                          <Input
                            name={"newPass"}
                            id={"newPass"}
                            onChange={(e) => handleResetPasswordChange(e)}
                            value={resetPasswordData.newPass}
                            variant={"main"}
                            type={showPass.pass ? "text" : "password"}
                            placeholder={"Enter your password"}
                          />
                          <InputRightElement pointerEvents={"auto"}>
                            <IconButton
                              display={"flex"}
                              aria-label="View Password"
                              variant={"unstyled"}
                              color={showPass.pass ? "main.blue" : "gray.400"}
                              icon={
                                showPass.pass ? (
                                  <HiOutlineEyeOff />
                                ) : (
                                  <HiOutlineEye />
                                )
                              }
                              fontSize={"md"}
                              size={"xs"}
                              onClick={() =>
                                setShowPass((s) => ({ ...s, pass: !s.pass }))
                              }
                              cursor={"pointer"}
                            />
                          </InputRightElement>
                        </InputGroup>
                        <FormHelperText></FormHelperText>
                      </FormControl>
                      <FormControl isRequired>
                        <FormLabel>Re-enter New Password</FormLabel>
                        <InputGroup>
                          <Input
                            name={"confNewPass"}
                            id={"confNewPass"}
                            onChange={(e) => handleResetPasswordChange(e)}
                            value={resetPasswordData.confNewPass}
                            variant={"main"}
                            type={showPass.confPass ? "text" : "password"}
                            placeholder={"Enter your password"}
                          />
                          <InputRightElement pointerEvents={"auto"}>
                            <IconButton
                              display={"flex"}
                              aria-label="View Password"
                              variant={"unstyled"}
                              color={
                                showPass.confPass ? "main.blue" : "gray.400"
                              }
                              icon={
                                showPass.confPass ? (
                                  <HiOutlineEyeOff />
                                ) : (
                                  <HiOutlineEye />
                                )
                              }
                              fontSize={"md"}
                              size={"xs"}
                              onClick={() =>
                                setShowPass((s) => ({
                                  ...s,
                                  confPass: !s.confPass,
                                }))
                              }
                              cursor={"pointer"}
                            />
                          </InputRightElement>
                        </InputGroup>
                        <FormHelperText></FormHelperText>
                      </FormControl>
                      {signInStatus && (
                        <Alert status={signInStatus.status}>
                          <AlertIcon />
                          {signInStatus.message}
                        </Alert>
                      )}
                      <Button
                        type={"submit"}
                        variant={"gradient"}
                        isLoading={loading}
                      >
                        Reset Password
                      </Button>
                    </Stack>
                  </form>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Container>
        </Flex>
      </Container>
    </>
  );
};

ResetPasswordPage.getLayout = (page: ReactElement) => {
  return (
    <>
      <Head>
        <title>
          Forget Password — NFT One — Explore finest digital collection soon
        </title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <MainIndex>{page}</MainIndex>
    </>
  );
};

export default ResetPasswordPage;
